/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Button,
    useBreakpointValue,
    Container,
    Stack,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useUnavailableProductsInCartContext} from '../../../contexts/unavailable-products-in-cart-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {sendClickEvent} from '../../../utils/gtm-utils'

const StickyCheckoutBar = ({basket}) => {
    const styles = useMultiStyleConfig('Cart')
    const [isVisible, setIsVisible] = useState(false)
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {UnavailableProductsInCart, unavailableProductIdsRef} =
        useUnavailableProductsInCartContext()

    useEffect(() => {
        const headerContainer = document.getElementById('app-header')
        const cartContainer = document.querySelector('[data-testid="sf-cart-container"]')
        let mobileObserver
        let domObserver
        let removeFallbackListeners = () => {}

        const getCheckoutButton = () => {
            return (
                cartContainer?.querySelector('[data-testid="cart-checkout-button"]') ||
                null
            )
        }

        const getElementTopFromDocument = (element) => {
            let top = 0
            let current = element

            while (current) {
                top += current.offsetTop || 0
                current = current.offsetParent
            }

            return top
        }

        const setupScrollFallback = (checkoutButton) => {
            const computeVisibility = () => {
                const ctaRect = checkoutButton.getBoundingClientRect()
                const headerHeight = headerContainer?.getBoundingClientRect()?.height || 0
                const viewportHeight = window.innerHeight || 0

                const isVisibleInViewport =
                    ctaRect.top < viewportHeight && ctaRect.bottom > headerHeight

                setIsVisible(!isVisibleInViewport)
            }

            window.addEventListener('scroll', computeVisibility, {passive: true})
            window.addEventListener('resize', computeVisibility)
            computeVisibility()

            removeFallbackListeners = () => {
                window.removeEventListener('scroll', computeVisibility)
                window.removeEventListener('resize', computeVisibility)
            }
        }

        const setupDesktopScrollTracking = (checkoutButton) => {
            const originalCtaBottomFromDocument =
                getElementTopFromDocument(checkoutButton) +
                (checkoutButton.offsetHeight || checkoutButton.getBoundingClientRect().height || 0)

            const computeVisibility = () => {
                const headerHeight = headerContainer?.getBoundingClientRect()?.height || 0
                const scrollPosition = window.scrollY || 0
                const viewportHeight = window.innerHeight || 0
                const ctaRect = checkoutButton.getBoundingClientRect()

                const isVisibleInViewport =
                    ctaRect.top < viewportHeight && ctaRect.bottom > headerHeight

                const hasScrolledPastOriginalCta =
                    scrollPosition + headerHeight > originalCtaBottomFromDocument

                // Desktop should show when CTA is off-screen (e.g. starts below the fold)
                // or after user has scrolled past its original position.
                setIsVisible(!isVisibleInViewport || hasScrolledPastOriginalCta)
            }

            window.addEventListener('scroll', computeVisibility, {passive: true})
            window.addEventListener('resize', computeVisibility)
            computeVisibility()

            removeFallbackListeners = () => {
                window.removeEventListener('scroll', computeVisibility)
                window.removeEventListener('resize', computeVisibility)
            }
        }

        const bindVisibilityTracking = () => {
            const checkoutButton = getCheckoutButton()
            if (!checkoutButton) {
                return false
            }

            if (!isMobile) {
                setupDesktopScrollTracking(checkoutButton)
                return true
            }

            if (typeof IntersectionObserver === 'undefined') {
                setupScrollFallback(checkoutButton)
                return true
            }

            mobileObserver = new IntersectionObserver(
                ([entry]) => {
                    // Show sticky bar only when page checkout CTA is not visible.
                    setIsVisible(!entry.isIntersecting)
                },
                {
                    threshold: 0.1,
                    rootMargin: '0px'
                }
            )

            mobileObserver.observe(checkoutButton)
            return true
        }

        const isBound = bindVisibilityTracking()

        if (!isBound) {
            setIsVisible(false)
            if (typeof MutationObserver !== 'undefined') {
                domObserver = new MutationObserver(() => {
                    if (bindVisibilityTracking()) {
                        domObserver.disconnect()
                    }
                })
                domObserver.observe(document.body, {childList: true, subtree: true})
            }
        }

        return () => {
            if (mobileObserver) {
                mobileObserver.disconnect()
            }
            if (domObserver) {
                domObserver.disconnect()
            }
            removeFallbackListeners()
        }
    }, [isMobile, basket?.basketId])

    const handleCheckout = () => {
        if (unavailableProductIdsRef.current?.length > 0) {
            UnavailableProductsInCart.onOpen()
        } else {
            sendClickEvent('checkout_now_clicked')
            navigate('/checkout')
        }
    }

    const giftCardTotal = (basket?.paymentInstruments || [])
        .filter((instrument) => instrument.paymentMethodId === 'SMARTCLIXX')
        .reduce((sum, instrument) => sum + (instrument.amount || 0), 0)

    // Calculate estimated total - match order-summary calculation logic
    // When orderTotal is null (e.g., with certain promotions), explicitly add shipping and tax
    const estimatedShipping = Number(basket?.shippingTotal) || 0
    const estimatedTax = basket?.taxTotal != null ? Number(basket.taxTotal) : 0
    const estimatedTotal =
        basket?.orderTotal != null
            ? Number(basket.orderTotal)
            : (Number(basket?.productTotal) || 0) + estimatedShipping + estimatedTax
    const totalAmount = estimatedTotal - giftCardTotal
    const shouldShow = isVisible && !!basket

    return (
        <Box
            {...styles.stickyBarRoot}
            transform={shouldShow ? 'translateY(0)' : 'translateY(100%)'}
            visibility={shouldShow ? 'visible' : 'hidden'}
            pointerEvents={shouldShow ? 'auto' : 'none'}
            aria-hidden={!shouldShow}
        >
            <Container {...styles.stickyBarContainer}>
                {isMobile ? (
                    // Mobile Layout: Stacked
                    <Stack {...styles.stickyBarMobileStack}>
                        {/* Top Row: Estimated Total and Amount */}
                        <Flex {...styles.stickyBarRow}>
                            <Text {...styles.stickyBarLabel}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                            <Text {...styles.stickyBarAmount}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={totalAmount}
                                />
                            </Text>
                        </Flex>
                        {/* Bottom Row: Checkout Button */}
                        <Button
                            {...styles.stickyBarMobileButton}
                            variant="solid"
                            onClick={handleCheckout}
                            size="lg"
                        >
                            <FormattedMessage
                                id="cart_cta.checkout_button"
                                defaultMessage="Checkout"
                            />
                        </Button>
                    </Stack>
                ) : (
                    // Desktop Layout: Horizontal
                    <Flex {...styles.stickyBarDesktopRow}>
                        {/* Left: Estimated Total */}
                        <Flex {...styles.stickyBarDesktopTotal}>
                            <Text {...styles.stickyBarLabel}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                                :
                            </Text>
                            <Text {...styles.stickyBarAmount}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={totalAmount}
                                />
                            </Text>
                        </Flex>
                        {/* Right: Checkout Button */}
                        <Button
                            {...styles.stickyBarDesktopButton}
                            variant="solid"
                            onClick={handleCheckout}
                            size="lg"
                        >
                            <FormattedMessage
                                id="cart_cta.checkout_button"
                                defaultMessage="Checkout"
                            />
                        </Button>
                    </Flex>
                )}
            </Container>
        </Box>
    )
}

StickyCheckoutBar.propTypes = {
    basket: PropTypes.object.isRequired
}

export default StickyCheckoutBar
